// Port of SuiteStory/StoryTimeSuggester.swift: proposes story times for scenes that have none, from what their openings
// say (cross-platform spec §10.1: story-time tags are proposed and accepted in bulk, never set silently).
//
// Scenes are read in narrative order with a running story clock. A scene with a story time sets the clock. Otherwise
// its opening (the first two paragraphs) is searched for an explicit date ("14 March 1983", "Tuesday, March 15"), then
// for a transition relative to the clock ("the next morning", "three weeks later", "that evening", "on Friday"), with
// any time of day ("at 3 pm", "dawn"). A proposal moves the clock on, so chains of "the next day" follow each other.
// Scenes with no cue get no proposal: the story views estimate those between tagged neighbours.
//
// Times are clock readings in `zone` (the device's): story times are wall-clock times at the setting, entered in the
// device's zone (see `sceneMoment`). Instants are milliseconds since 1970.
import { plainText } from '../proseMarkdown.ts';
import { graphemes, trimCFWhitespace } from '../unicode.ts';
import { firstMatchIn, icu, matchesIn } from '../text/icuRegex.ts';
import { sentenceRanges } from '../text/sentences.ts';
import type { SceneFacts } from './sceneFacts.ts';
import { adding, components, deviceZone, instant, nextWeekday, settingHour, type CalendarUnit, type Zone } from './zones.ts';

export interface StoryTimeScene {
  id: string;
  title: string;
  /** Plain text (markup removed), paragraphs separated by blank lines. */
  text: string;
  storyTime: number | null;
}

export interface StoryTimeProposal {
  sceneID: string;
  title: string;
  date: number;
  basis: 'explicit' | 'relative';
  /** The words it came from, as written. */
  evidence: string;
  /** The sentence they're in (at most 200 characters), to judge the suggestion by. */
  context: string;
  /** Where they are in the scene's opening (UTF-16 [start, end)). */
  range: [number, number];
}

/** `StoryTimeSuggester.scenes`: the manuscript's scenes as the suggester reads them (plain text, in order). */
export function storyTimeScenes(facts: SceneFacts[]): StoryTimeScene[] {
  return facts.map((f) => ({ id: f.id, title: f.title, text: plainText(f.text), storyTime: f.start }));
}

/** `StoryTimeSuggester.suggest`: proposals in narrative order. `start` is when the story begins, for relative cues
 *  before the first dated scene. */
export function suggestStoryTimes(scenes: StoryTimeScene[], options: { start?: number | null; zone?: Zone } = {}): StoryTimeProposal[] {
  const zone = options.zone ?? deviceZone();
  let clock = options.start ?? null;
  const proposals: StoryTimeProposal[] = [];
  for (const scene of scenes) {
    if (scene.storyTime !== null) { clock = scene.storyTime; continue; }
    const text = opening(scene.text);
    const explicit = explicitDate(text, clock, zone);
    if (explicit) {
      const [date, range] = explicit;
      const context = sentenceAround(range, text);
      const when = timeOfDay(context, date, zone) ?? date;
      proposals.push({ sceneID: scene.id, title: scene.title, date: when, basis: 'explicit', evidence: text.slice(range[0], range[1]), context, range });
      clock = when;
      continue;
    }
    if (clock === null) continue;
    const rel = relative(text, clock, zone);
    if (rel) {
      const [date, range] = rel;
      proposals.push({ sceneID: scene.id, title: scene.title, date, basis: 'relative', evidence: text.slice(range[0], range[1]), context: sentenceAround(range, text), range });
      clock = date;
    }
  }
  return proposals;
}

/** The sentence around a range (trimmed; at most 200 characters). */
function sentenceAround(range: [number, number], text: string): string {
  const hit = sentenceRanges(text).find(([a, b]) => range[0] >= a && range[0] < b) ?? range;
  const s = text.slice(hit[0], hit[1]).replace(/^\s+|\s+$/g, '');
  const chars = Array.from(s);
  return chars.length > 200 ? chars.slice(0, 199).join('') + '…' : s;
}

/** The first two paragraphs (openings carry the transitions), at most 800 characters. */
export function opening(text: string): string {
  const paragraphs = text.split('\n').filter((p) => trimCFWhitespace(p) !== '');
  return graphemes(paragraphs.slice(0, 2).join('\n')).slice(0, 800).join('');
}

// MARK: Explicit dates

const MONTHS = ['january', 'february', 'march', 'april', 'may', 'june', 'july', 'august', 'september', 'october', 'november', 'december'];
const MONTH = '(January|February|March|April|May|June|July|August|September|October|November|December|Jan|Feb|Mar|Apr|Jun|Jul|Aug|Sept?|Oct|Nov|Dec)\\.?';
/** "14 March 1983", "14th of March", "March 14, 1983", "March 14th". */
const DAY_MONTH = icu(String.raw`\b(\d{1,2})(?:st|nd|rd|th)?(?: of)? ` + MONTH + String.raw`(?:,? (\d{4}))?\b`);
const MONTH_DAY = icu(String.raw`\b` + MONTH + String.raw` (\d{1,2})(?:st|nd|rd|th)?(?:,? (\d{4}))?\b`);
/** "1983-03-14". */
const ISO = icu(String.raw`\b(\d{4})-(\d{2})-(\d{2})\b`);

function monthNumber(name: string | undefined): number | null {
  if (name === undefined) return null;
  const lower = name.toLowerCase().replace(/^\.+|\.+$/g, '').slice(0, 3);
  const i = MONTHS.findIndex((m) => m.startsWith(lower));
  return i >= 0 ? i + 1 : null;
}

const int = (s: string | undefined) => (s !== undefined && /^\d+$/.test(s) ? Number(s) : null);

function explicitDate(text: string, clock: number | null, zone: Zone): [number, [number, number]] | null {
  const found: [number, [number, number]][] = [];
  const add = (day: number | null, month: number | null, year: number | null, range: [number, number]) => {
    // "May" and "March" are also words: a bare "May 5" needs a clock or a year to settle the year.
    if (day === null || month === null || day < 1 || day > 31) return;
    const y = year ?? (clock !== null ? components(clock, zone).year : null);
    if (y === null) return;
    found.push([instant({ year: y, month, day, hour: 9, minute: 0, second: 0, ms: 0 }, zone), range]);
  };
  const range = (m: RegExpExecArray): [number, number] => [m.index, m.index + m[0].length];
  for (const m of matchesIn(DAY_MONTH, text)) add(int(m[1]), monthNumber(m[2]), int(m[3]), range(m));
  for (const m of matchesIn(MONTH_DAY, text)) add(int(m[2]), monthNumber(m[1]), int(m[3]), range(m));
  for (const m of matchesIn(ISO, text)) add(int(m[3]), int(m[2]), int(m[1]), range(m));
  // `min(by:)`: the first of the earliest.
  let best: [number, [number, number]] | null = null;
  for (const f of found) if (!best || f[1][0] < best[1][0]) best = f;
  return best;
}

// MARK: Relative cues

const NUMBERS: Record<string, number> = {
  a: 1, an: 1, one: 1, two: 2, three: 3, four: 4, five: 5, six: 6, seven: 7, eight: 8, nine: 9, ten: 10, eleven: 11, twelve: 12,
  'a couple of': 2, 'a few': 3,
};
const NUMBER = '(\\d+|a couple of|a few|an?|one|two|three|four|five|six|seven|eight|nine|ten|eleven|twelve)';
const LATER = icu(String.raw`\b` + NUMBER + String.raw` (hour|day|week|month|year)s? (later|afterwards|on(?=[,.;]))`, 'i');
const NEXT_DAY = icu(String.raw`\b(the )?(next|following) (morning|day|afternoon|evening|night)\b|\bthe day after\b`, 'i');
const NEXT_PERIOD = icu(String.raw`\b(the )?(next|following) (week|month|year)\b`, 'i');
const SAME_DAY = icu(String.raw`\b(later )?(that|this) (morning|afternoon|evening|night)\b|\blater that day\b`, 'i');
/** Only opening a sentence ("On Friday, …", "That Sunday"): mid-sentence, a weekday is as often a plan or a habit. */
const WEEKDAY = icu(String.raw`(?:^|(?<=[.!?…”"]\s)|(?<=\n))(on|next|that|by) (Monday|Tuesday|Wednesday|Thursday|Friday|Saturday|Sunday)\b`, 'im');
const WEEKDAYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
const UNITS: Record<string, CalendarUnit> = { hour: 'hour', day: 'day', week: 'week', month: 'month', year: 'year' };

function relative(text: string, clock: number, zone: Zone): [number, [number, number]] | null {
  const candidates: [number, [number, number]][] = [];
  const range = (m: RegExpExecArray): [number, number] => [m.index, m.index + m[0].length];
  const lower = (m: RegExpExecArray, i: number) => m[i]?.toLowerCase();
  // A time of day belongs to the cue's own sentence, not to whatever else the opening mentions.
  const timeFor = (m: RegExpExecArray, date: number, keepTime = false) => timeOfDay(sentenceAround(range(m), text), date, zone, keepTime);

  const later = firstMatchIn(LATER, text);
  if (later) {
    const n = lower(later, 1)!, unit = lower(later, 2)!;
    const count = int(n) ?? NUMBERS[n] ?? 1;
    let date = adding(UNITS[unit], count, clock, zone);
    // Days or more later starts a new day: the morning, unless a time is given.
    if (unit !== 'hour') date = at('morning', date, zone) ?? date;
    candidates.push([timeFor(later, date, unit === 'hour') ?? date, range(later)]);
  }
  const nextDay = firstMatchIn(NEXT_DAY, text);
  if (nextDay) {
    const date = adding('day', 1, clock, zone);
    const part = lower(nextDay, 3) ?? 'day';
    const morning = at('morning', date, zone) ?? date;
    candidates.push([at(part, date, zone) ?? timeFor(nextDay, date) ?? morning, range(nextDay)]);
  }
  const nextPeriod = firstMatchIn(NEXT_PERIOD, text);
  if (nextPeriod) {
    const unit = lower(nextPeriod, 3)!;
    const date = at('morning', adding(UNITS[unit], 1, clock, zone), zone);
    if (date !== null) candidates.push([date, range(nextPeriod)]);
  }
  const sameDay = firstMatchIn(SAME_DAY, text);
  if (sameDay) {
    const part = lower(sameDay, 3) ?? 'afternoon';
    const date = at(part, clock, zone);
    if (date !== null && date > clock) candidates.push([date, range(sameDay)]);
  }
  const weekday = firstMatchIn(WEEKDAY, text);
  if (weekday) {
    const index = WEEKDAYS.indexOf(lower(weekday, 2)!);
    if (index >= 0) {
      // The next such day after the clock (a week on if it's today and past nine).
      const date = nextWeekday(clock, index + 1, 9, zone);
      candidates.push([timeFor(weekday, date) ?? date, range(weekday)]);
    }
  }
  // The cue that comes first in the scene is the one that sets it.
  let best: [number, [number, number]] | null = null;
  for (const c of candidates) if (!best || c[1][0] < best[1][0]) best = c;
  return best;
}

// MARK: Times of day

const PARTS: Record<string, number> = {
  dawn: 6, sunrise: 6, morning: 9, noon: 12, midday: 12, lunchtime: 13, afternoon: 15, dusk: 18, sunset: 18, evening: 19, night: 22, midnight: 0,
};
const CLOCK_TIME = icu(String.raw`\b(\d{1,2})(?:[:.](\d{2}))? ?(am|pm|a\.m\.|p\.m\.|o'clock|o’clock)`, 'i');
const PART_OF_DAY = icu(String.raw`\b(dawn|sunrise|morning|noon|midday|lunchtime|afternoon|dusk|sunset|evening|night|midnight)\b`, 'i');

function at(part: string, date: number, zone: Zone): number | null {
  const hour = PARTS[part];
  return hour === undefined ? null : settingHour(hour, 0, 0, date, zone);
}

/** A clock time ("at 3.30 pm"; "ten o'clock" isn't read) or part of day ("that evening") in the text, on the given day.
 *  `keepTime` leaves the date's own time unless a clock time is written. */
export function timeOfDay(text: string, date: number, zone: Zone, keepTime = false): number | null {
  const m = firstMatchIn(CLOCK_TIME, text);
  if (m) {
    let hour = Number(m[1]);
    if (hour <= 23) {
      const minute = m[2] !== undefined ? Number(m[2]) : 0;
      const suffix = m[3].toLowerCase();
      if (suffix.startsWith('p') && hour < 12) hour += 12;
      if (suffix.startsWith('a') && hour === 12) hour = 0;
      if (suffix.startsWith('o') && hour < 7) hour += 12; // "three o'clock" in a story is usually the afternoon
      return settingHour(hour, minute, 0, date, zone);
    }
  }
  if (keepTime) return null;
  const part = firstMatchIn(PART_OF_DAY, text);
  return part ? at(part[0].toLowerCase(), date, zone) : null;
}
